import type { TFunction } from 'i18next'
import type { ProblemDetails } from '@apiary/shared'
import { ApiError, NetworkError } from './api-error'

/**
 * `problem+json` → человеческий текст (docs/10 §8: «по-человечески, без
 * канцелярита», «ошибки — всегда с действием»).
 *
 * Единственный источник ветвления — `problem.code`. Текст берётся из i18n,
 * `meta` подставляется в интерполяцию (`LIMIT_EXCEEDED`, `UNSUPPORTED_MC_VERSION`).
 */

export interface HumanProblem {
  title: string
  detail: string
  /** Подпись кнопки-действия; пустая строка — действия нет. */
  action: string
  /** Технические подробности — во второй план (tooltip / «подробнее»). */
  technical: string | null
  code: string | null
}

export function describeProblem(problem: ProblemDetails, t: TFunction): HumanProblem {
  const meta = problem.meta ?? {}
  const interpolation: Record<string, unknown> = {
    ...meta,
    supported: Array.isArray(meta.supported) ? meta.supported.join(', ') : '',
    seconds: typeof meta.retryAfter === 'number' ? meta.retryAfter : 30
  }

  const title = t(`errors.code.${problem.code}.title`, interpolation)
  const localizedDetail = t(`errors.code.${problem.code}.detail`, interpolation)
  // Серверный `detail` конкретнее шаблона («Аккаунт «main» занят ботом…»),
  // поэтому если он есть — показываем его.
  const detail =
    problem.detail !== undefined && problem.detail !== '' ? problem.detail : localizedDetail

  return {
    title,
    detail,
    action: t(`errors.action.${problem.code}`),
    technical: problem.traceId === undefined ? null : t('errors.traceId', { id: problem.traceId }),
    code: problem.code
  }
}

/** Любая ошибка запроса → текст для UI. Сеть и неизвестное тоже покрыты. */
export function describeError(error: unknown, t: TFunction): HumanProblem {
  if (error instanceof ApiError) return describeProblem(error.problem, t)
  if (error instanceof NetworkError) {
    return {
      title: t('errors.networkTitle'),
      detail: t('errors.networkDetail'),
      action: t('common.retry'),
      technical: null,
      code: null
    }
  }
  return {
    title: t('errors.unexpectedTitle'),
    detail: error instanceof Error ? error.message : String(error),
    action: t('common.retry'),
    technical: null,
    code: null
  }
}
